import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/utils/lib";

import { Spinner } from "../shared/spinner";
import { Slot } from "./slot";

const buttonVariants = cva(
  `
    inline-flex
    items-center
    justify-center rounded-full font-sans! text-sm font-medium whitespace-nowrap
    ring-offset-background transition-colors focus-visible:ring-2
    focus-visible:ring-foreground focus-visible:ring-offset-2
    focus-visible:outline-hidden
    disabled:pointer-events-none disabled:opacity-50
  `,
  {
    variants: {
      variant: {
        default: `
          border border-transparent
          bg-primary text-primary-foreground hover:border-primary
          hover:bg-primary-foreground
          hover:text-primary
          data-popup-open:border-primary
          data-popup-open:bg-primary-foreground
          data-popup-open:text-primary
          data-[state=open]:border-primary
          data-[state=open]:bg-primary-foreground data-[state=open]:text-primary
        `,
        destructive: `
          border border-transparent
          bg-destructive text-destructive-foreground
          hover:border-destructive hover:bg-transparent
          hover:text-destructive
          data-popup-open:border-destructive
          data-popup-open:bg-transparent
          data-popup-open:text-destructive
          data-[state=open]:border-destructive
          data-[state=open]:bg-transparent
          data-[state=open]:text-destructive
        `,
        success: `
          bg-green-600 text-white
          hover:bg-green-600/90
          data-popup-open:bg-green-600/90
          data-[state=open]:bg-green-600/90
        `,
        successPrimary: `
          border
          border-green-600 bg-transparent text-green-600 hover:border-green-600/90
          hover:bg-card hover:text-green-600/90
          data-popup-open:border-green-600/90
          data-popup-open:bg-card
          data-popup-open:text-green-600/90
          data-[state=open]:border-green-600/90
          data-[state=open]:bg-card
          data-[state=open]:text-green-600/90
        `,
        error: `
          border
          border-red-600 bg-transparent text-red-600 hover:border-red-600/90
          hover:bg-card hover:text-red-600/90
          data-popup-open:border-red-600/90
          data-popup-open:bg-card
          data-popup-open:text-red-600/90
          data-[state=open]:border-red-600/90
          data-[state=open]:bg-card
          data-[state=open]:text-red-600/90
        `,
        outline: `
          border border-border-light
          bg-transparent text-foreground
          hover:bg-card
          hover:text-muted-foreground
          data-popup-open:bg-card
          data-popup-open:text-muted-foreground
          data-[state=open]:bg-card data-[state=open]:text-muted-foreground
        `,
        outlinePrimary: `
          border border-primary
          bg-transparent text-primary
          hover:bg-card
          hover:text-primary
          data-popup-open:bg-card
          data-popup-open:text-primary
          data-[state=open]:bg-card data-[state=open]:text-primary
        `,
        secondary: `
          border border-border-light bg-secondary text-muted-foreground
          hover:opacity-80
          data-popup-open:opacity-80
          data-[state=open]:opacity-80
        `,
        ghost: `
          text-muted
          hover:bg-transparent hover:text-primary
          data-popup-open:bg-transparent
          data-popup-open:text-primary
          data-[state=open]:bg-transparent
          data-[state=open]:text-primary
        `,
        link: `
          button-link text-primary underline-offset-4
          hover:underline
          data-popup-open:underline
          data-[state=open]:underline
        `,
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-6 px-3 text-xs font-normal",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        xl: "h-14 px-8",
        icon: "size-10",
        tooltip: "size-5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  as?: string;
}

const Button = ({
  ref,
  as = "button",
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  ...props
}: ButtonProps & {
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  const { nativeButton, ...buttonProps } = props as typeof props & {
    nativeButton?: boolean;
  };
  void nativeButton;
  const Comp = asChild ? Slot : as;
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      disabled={buttonProps.disabled || loading}
      type="button"
      {...buttonProps}
    >
      {loading ? (
        <div className="flex items-center gap-2">
          <Spinner />
        </div>
      ) : (
        buttonProps.children
      )}
    </Comp>
  );
};
Button.displayName = "Button";

export { Button, buttonVariants };
